import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { aedStr, ilsStr, eurStr, usdStr, pct, round } from '../lib/analysisEngine';
import { CHART_COLORS as C, compact } from '../lib/uiFormat';
import { useI18n } from '../lib/i18n';

const STR_LINE = '#0EA894';   // short-term = teal (matches CHART_COLORS.equity)
const LTR_LINE = C.rent;      // long-term = muted forest

// Small pill used in the (dark) hero when the STR strategy is active — styled for a
// photo/gradient background, matching the other hero tag chips.
export function StrategyBadge({ strategy }) {
  const { t } = useI18n();
  const cls = 'text-[10px] uppercase tracking-widest font-semibold bg-white/10 backdrop-blur border border-white/20 text-white rounded-full px-2.5 py-1';
  if (strategy === 'str') return <span className={cls}>{t('short_term_rental')}</span>;
  if (strategy === 'compare') return <span className={cls}>{t('ltr_vs_str_badge')}</span>;
  return null;
}

function Card({ label, value, sub, accent = 'gray' }) {
  const c = { green: 'text-green-600', amber: 'text-amber-600', sky: 'text-sky-600', red: 'text-red-600', gray: 'text-gray-900' }[accent] || 'text-gray-900';
  return (
    <div className="bg-white rounded-[1.75rem] border border-black/[0.06] shadow-premium p-4 flex flex-col justify-between min-h-[100px]">
      <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">{label}</p>
      <div>
        <p className={`text-xl md:text-2xl font-bold tabular-nums ${c} leading-tight`}>{value}</p>
        {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// STR summary cards — gross, expenses, net, net yield, payback vs LTR.
export function StrSummaryCards({ a, ils, eur, usd }) {
  const { t } = useI18n();
  const s = a.str;
  if (!s) return null;
  const netAccent = s.net >= 0 ? 'green' : 'red';
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
      <Card label={t('gross_revenue_yr')} accent="green" value={aedStr(s.gross)} sub={`${ilsStr(s.gross * ils)}${eur != null ? ' · ' + eurStr(s.gross * eur) : ''}${usd != null ? ' · ' + usdStr(s.gross * usd) : ''} · ${t('x_occ', { x: s.occupancy })}`} />
      <Card label={t('total_expenses_yr')} accent="amber" value={aedStr(s.totalExpenses)} sub={`${ilsStr(s.totalExpenses * ils)}${eur != null ? ' · ' + eurStr(s.totalExpenses * eur) : ''}${usd != null ? ' · ' + usdStr(s.totalExpenses * usd) : ''} · ${t('x_of_gross', { x: s.gross > 0 ? pct(s.totalExpenses / s.gross * 100) : '—' })}`} />
      <Card label={t('net_income_yr')} accent={netAccent} value={aedStr(s.net)} sub={`${ilsStr(s.net * ils)}${eur != null ? ' · ' + eurStr(s.net * eur) : ''}${usd != null ? ' · ' + usdStr(s.net * usd) : ''}`} />
      <Card label={t('net_yield')} accent={s.netYield >= 0 ? 'green' : 'red'} value={pct(s.netYield)} sub={t('on_total_cost_furnishing')} />
      <Card label={t('payback_vs_lt')} accent="sky"
        value={s.payback != null && isFinite(s.payback) ? t('x_yr', { x: s.payback.toFixed(1) }) : '—'}
        sub={s.payback != null && isFinite(s.payback) ? t('to_recoup_furnishing') : t('str_le_ltr_net')} />
    </div>
  );
}

// Full STR expense breakdown table: monthly / yearly / ILS / % of gross, with totals.
export function StrExpenseTable({ a, ils, eur, usd }) {
  const { t } = useI18n();
  const s = a.str;
  if (!s) return null;
  const gross = s.gross || 0;
  const pctOf = (y) => gross > 0 ? pct(y / gross * 100) : '—';
  const Row = ({ label, yearly, variable, strong, tone }) => (
    <tr className={`border-b border-gray-50 ${strong ? 'font-semibold' : ''} ${tone === 'amber' ? 'bg-amber-50/40' : tone === 'green' ? 'bg-green-50/40' : ''}`}>
      <td className="py-2 pr-3 text-gray-700">{label}{variable && <span className="ml-1.5 text-[9px] bg-gray-100 text-gray-400 px-1 py-0.5 rounded">{t('scales')}</span>}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-500">{aedStr(yearly / 12)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-800">{aedStr(yearly)}</td>
      <td className="py-2 px-3 text-right tabular-nums text-gray-400">{ilsStr(yearly * ils)}</td>
      {eur != null && <td className="py-2 px-3 text-right tabular-nums text-gray-400">{eurStr(yearly * eur)}</td>}
      {usd != null && <td className="py-2 px-3 text-right tabular-nums text-gray-400">{usdStr(yearly * usd)}</td>}
      <td className="py-2 pl-3 text-right tabular-nums text-gray-500">{pctOf(yearly)}</td>
    </tr>
  );
  return (
    <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06] mb-6">
      <h3 className="font-semibold text-gray-800 mb-1">{t('str_expense_breakdown')}</h3>
      <p className="text-xs text-gray-400 mb-4">{t('str_year1_line', { occ: s.occupancy, nights: round(s.occupiedNights), adr: aedStr(s.adr) })}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-gray-400 border-b border-black/[0.06]">
              <th className="py-2 pr-3 font-medium">{t('line_item')}</th>
              <th className="py-2 px-3 font-medium text-right">{t('month_col')}</th>
              <th className="py-2 px-3 font-medium text-right">{t('year_col')}</th>
              <th className="py-2 px-3 font-medium text-right">{t('ils_yr')}</th>
              {eur != null && <th className="py-2 px-3 font-medium text-right">€/yr</th>}
              {usd != null && <th className="py-2 px-3 font-medium text-right">$/yr</th>}
              <th className="py-2 pl-3 font-medium text-right">{t('pct_of_gross')}</th>
            </tr>
          </thead>
          <tbody>
            <Row label={t('gross_rental_revenue')} yearly={gross} strong tone="green" />
            {s.lineItems.map(li => <Row key={li.key} label={t('str_li_' + li.key) !== 'str_li_' + li.key ? t('str_li_' + li.key) : li.label} yearly={li.yearly} variable={li.variable} />)}
            <Row label={t('total_expenses')} yearly={s.totalExpenses} strong tone="amber" />
            <tr className="font-bold">
              <td className="py-2.5 pr-3 text-gray-900">{t('net_income')}</td>
              <td className="py-2.5 px-3 text-right tabular-nums text-gray-500">{aedStr(s.net / 12)}</td>
              <td className={`py-2.5 px-3 text-right tabular-nums ${s.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>{aedStr(s.net)}</td>
              <td className="py-2.5 px-3 text-right tabular-nums text-gray-400">{ilsStr(s.net * ils)}</td>
              {eur != null && <td className="py-2.5 px-3 text-right tabular-nums text-gray-400">{eurStr(s.net * eur)}</td>}
              {usd != null && <td className="py-2.5 px-3 text-right tabular-nums text-gray-400">{usdStr(s.net * usd)}</td>}
              <td className="py-2.5 pl-3 text-right tabular-nums text-gray-500">{pctOf(s.net)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Compare-both: LTR vs STR side by side + net-income chart + verdict + breakeven occupancy.
export function StrVsLtr({ ltr, str, ils, eur, usd }) {
  const { t, dir } = useI18n();
  if (!ltr || !str || !str.str) return null;
  const s = str.str;
  const ltrGross = ltr.startRent;
  const ltrExp = ltr.scYearly;
  const ltrNet = ltrGross - ltrExp;
  const rows = [
    { label: t('gross_income_yr'), ltr: ltrGross, str: s.gross, fmt: 'aed', best: 'max' },
    { label: t('total_expenses_yr'), ltr: ltrExp, str: s.totalExpenses, fmt: 'aed', best: 'min' },
    { label: t('net_income_yr'), ltr: ltrNet, str: s.net, fmt: 'aed', best: 'max' },
    { label: t('net_yield'), ltr: ltr.netYield, str: s.netYield, fmt: 'pct', best: 'max' },
  ];
  const fmt = (v, f) => f === 'pct' ? pct(v) : aedStr(v);
  const strWins = s.net >= ltrNet;
  const yieldDiff = s.netYield - ltr.netYield;

  // merged net-income series by year
  const data = str.years.filter(y => y.ysh >= 1).map(y => {
    const l = ltr.years.find(x => x.year === y.year);
    return { year: y.year, str: round(y.netRent), ltr: l ? round(l.netRent) : null };
  });
  const handoverYear = str.atHandover?.year;

  return (
    <div className="bg-white rounded-[1.75rem] p-4 md:p-5 shadow-premium border border-black/[0.06] mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="font-semibold text-gray-800">{t('ltr_vs_str')}</h3>
        <StrategyBadge strategy="compare" />
      </div>

      {/* verdict banner */}
      <div className={`rounded-xl p-4 mb-4 text-white bg-gradient-to-br ${strWins ? 'from-sky-500 to-blue-600' : 'from-violet-500 to-purple-600'}`}>
        <p className="text-sm font-semibold">
          {strWins
            ? t('str_nets_more', { x: aedStr(s.extraVsLtr) })
            : t('ltr_nets_more', { x: aedStr(-s.extraVsLtr) })}
          <span className="opacity-90 font-normal"> · <span className="tabular-nums">{yieldDiff >= 0 ? '+' : ''}{yieldDiff.toFixed(1)}%</span> {t('net_yield')} {yieldDiff >= 0 ? t('yield_in_str_favour') : t('yield_in_ltr_favour')}</span>
        </p>
        {s.breakevenOcc != null && (
          <p className="text-xs opacity-90 mt-1">
            {t('breakeven_occ_line', { occ: (s.breakevenOcc <= 100 && s.breakevenOcc >= 0 ? pct(s.breakevenOcc) : (s.breakevenOcc > 100 ? '>100%' : '—')), assume: s.occupancy + '%' })}
          </p>
        )}
      </div>

      {/* side-by-side table */}
      <div className="overflow-x-auto mb-5">
        <table className="w-full text-sm min-w-[420px]">
          <thead>
            <tr className="text-left text-gray-400 border-b border-black/[0.06]">
              <th className="py-2 pr-3 font-medium">{t('metric')}</th>
              <th className="py-2 px-3 font-medium text-right"><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: LTR_LINE }} />{t('long_term')}</th>
              <th className="py-2 pl-3 font-medium text-right"><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: STR_LINE }} />{t('short_term')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const strBest = r.best === 'max' ? r.str >= r.ltr : r.str <= r.ltr;
              return (
                <tr key={i} className="border-b border-gray-50">
                  <td className="py-2 pr-3 text-gray-600">{r.label}</td>
                  <td className={`py-2 px-3 text-right tabular-nums ${!strBest ? 'bg-green-50 text-green-700 font-semibold rounded' : 'text-gray-800'}`}>{fmt(r.ltr, r.fmt)}</td>
                  <td className={`py-2 pl-3 text-right tabular-nums ${strBest ? 'bg-green-50 text-green-700 font-semibold rounded' : 'text-gray-800'}`}>{fmt(r.str, r.fmt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* net-income over time */}
      <p className="text-xs text-gray-400 mb-2">{t('net_income_over_horizon')}</p>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="year" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={compact} width={44} />
          <Tooltip content={<CompareTooltip ils={ils} eur={eur} usd={usd} />} />
          <Legend wrapperStyle={{ fontSize: 12 }} align={dir === 'rtl' ? 'right' : 'left'} />
          {handoverYear != null && <ReferenceLine x={handoverYear} stroke={C.handover} strokeDasharray="4 3" label={{ value: t('handover'), position: 'insideTopRight', fontSize: 10, fill: C.handover, fontWeight: 600 }} />}
          <Line type="monotone" dataKey="ltr" name={t('long_term_net')} stroke={LTR_LINE} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
          <Line type="monotone" dataKey="str" name={t('short_term_net')} stroke={STR_LINE} strokeWidth={2.5} dot={false} connectNulls isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function CompareTooltip({ active, payload, label, ils, eur, usd }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs min-w-[150px]">
      <p className="font-semibold text-gray-700 mb-1.5">{label}</p>
      {payload.filter(p => p.value != null).map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color || p.stroke }} />
          <span className="text-gray-500">{p.name}</span>
          <span className="ml-auto text-right leading-tight">
            <span className="font-medium text-gray-800 block">{aedStr(p.value)}</span>
            <span className="text-[10px] text-gray-400 block">{ilsStr(p.value * ils)}</span>
            {eur != null && <span className="text-[10px] text-gray-400 block">{eurStr(p.value * eur)}</span>}
            {usd != null && <span className="text-[10px] text-gray-400 block">{usdStr(p.value * usd)}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}
